import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { addWorkTypeRate, deleteWorkTypeRate } from '../../services/api';
import { formatDate, formatEur } from '../../lib/workStatus';
import { Trash2, History } from 'lucide-react';
import { toast } from 'sonner';

const today = () => new Date().toISOString().split('T')[0];

export const RateHistoryDialog = ({ open, onOpenChange, workType, onChanged }) => {
  const [form, setForm] = useState({ rate: '', valid_from: today(), note: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ rate: workType?.rate != null ? String(workType.rate) : '', valid_from: today(), note: '' });
  }, [open, workType]);

  if (!workType) return null;
  const now = new Date();
  const currentId = workType.rates.find((r) => new Date(r.valid_from) <= now)?.id;

  const submit = async (e) => {
    e.preventDefault();
    if (form.rate === '' || Number(form.rate) < 0) { toast.error('Ievadiet likmi'); return; }
    setSaving(true);
    try {
      const updated = await addWorkTypeRate(workType.key, { rate: Number(form.rate), valid_from: `${form.valid_from}T00:00:00`, note: form.note || null });
      toast.success('Jaunā likme saglabāta');
      onChanged(updated);
      setForm((p) => ({ ...p, note: '' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās saglabāt');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rateId) => {
    if (!window.confirm('Dzēst šo likmes ierakstu? Jau bloķētās algas tas neietekmē.')) return;
    try {
      onChanged(await deleteWorkTypeRate(workType.key, rateId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās dzēst');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="rate-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5" /> {workType.label} — likmju vēsture</DialogTitle>
          <DialogDescription>Jaunā likme attiecas uz darba ierakstiem ar datumu no norādītā datuma. Pārbaudītu mēnešu aprēķini nemainās.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-4 gap-2 items-end rounded-xl bg-[#f8faf8] p-3" data-testid="rate-history-form">
          <div className="space-y-1"><Label className="text-xs">Likme (€ / {workType.unit})</Label><Input type="number" step="0.01" min="0" value={form.rate} onChange={(e) => setForm((p) => ({ ...p, rate: e.target.value }))} data-testid="rate-history-rate" /></div>
          <div className="space-y-1"><Label className="text-xs">Spēkā no</Label><Input type="date" value={form.valid_from} onChange={(e) => setForm((p) => ({ ...p, valid_from: e.target.value }))} data-testid="rate-history-valid-from" /></div>
          <div className="space-y-1"><Label className="text-xs">Piezīme</Label><Input value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} data-testid="rate-history-note" /></div>
          <Button type="submit" disabled={saving} className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="rate-history-submit">Pievienot</Button>
        </form>
        <div className="divide-y text-sm max-h-72 overflow-y-auto" data-testid="rate-history-list">
          {workType.rates.map((r) => (
            <div key={r.id} className="flex items-center justify-between py-2" data-testid={`rate-history-row-${r.id}`}>
              <div>
                <span className="font-semibold">{formatEur(r.rate)}</span> <span className="text-[#2E2E2E]/60">/ {workType.unit} · no {formatDate(r.valid_from)}</span>
                {r.id === currentId && <Badge className="ml-2 bg-green-100 text-green-700">spēkā</Badge>}
                {new Date(r.valid_from) > now && <Badge className="ml-2 bg-blue-100 text-blue-700">nākotnē</Badge>}
                <div className="text-xs text-[#2E2E2E]/50">{r.note ? `${r.note} · ` : ''}{r.set_by}</div>
              </div>
              {workType.rates.length > 1 && (
                <Button size="sm" variant="ghost" onClick={() => remove(r.id)} data-testid={`rate-history-delete-${r.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
